import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, questionBanks, testSchemas, invitations } from '../api';
import pricing from '../config/pricing.json';

const Ico = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);
const Check = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
);
const ICONS = {
  bank: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z',
  schema: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
  mail: 'M4 4h16v16H4zM22 6l-10 7L2 6',
  card: 'M2 5h20v14H2zM2 10h20',
  plus: 'M12 5v14M5 12h14',
  invite: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM19 8v6M22 11h-6',
  chart: 'M3 3v18h18M18 17V9M13 17V5M8 17v-3',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  chev: 'M9 6l6 6-6 6',
};

const STATUS = {
  completed: { cls: 'pass', label: 'Завершён' },
  in_progress: { cls: 'part', label: 'В процессе' },
  pending: { cls: 'sent', label: 'Ожидает' },
};

function Dashboard() {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [tariff, setTariff] = useState('free');
  const [res, setRes] = useState(null); // resources-stats
  const [banks, setBanks] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [invMap, setInvMap] = useState({});
  const [openBanks, setOpenBanks] = useState(new Set());
  const [openSchemas, setOpenSchemas] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError(false);
      const [me, qb, ts] = await Promise.all([
        auth.getMe().catch(() => null),
        questionBanks.getAll(),
        testSchemas.getAll(),
      ]);
      if (me) setCompany(me.data);
      setBanks(qb.data);
      setSchemas(ts.data);
      auth.getTariffInfo().then((r) => setTariff(r.data.tariff || 'free')).catch(() => {});
      auth.getResourcesStats().then((r) => setRes(r.data)).catch(() => {});
      const map = {};
      for (const s of ts.data) {
        try { map[s.id] = (await invitations.getByTestSchema(s.id)).data; } catch { map[s.id] = []; }
      }
      setInvMap(map);
    } catch (e) {
      console.error('dashboard load failed', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const totalInv = Object.values(invMap).reduce((a, arr) => a + arr.length, 0);
  const limits = pricing.tariffs[tariff]?.limits || pricing.tariffs.free.limits;

  const quota = (key, used, purchased = 0) => {
    const base = limits[key];
    const limit = base == null ? null : base + purchased;
    const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    const cls = limit == null ? '' : (used >= limit ? 'full' : pct >= 80 ? 'warn' : '');
    return { limit, pct, cls, used };
  };
  const qBanks = quota('question_banks', res?.question_banks ?? banks.length, res?.purchased_question_banks);
  const qSchemas = quota('test_schemas', res?.test_schemas ?? schemas.length, res?.purchased_test_schemas);
  const qGroups = quota('group_invitations', res?.group_invitations ?? 0, res?.purchased_group_invitations);
  const nearLimit = [qBanks, qSchemas, qGroups].some((q) => q.limit != null && q.used >= q.limit);

  const QuotaCell = ({ label, q }) => (
    <div className="q">
      <div className="qh">
        <span className="qn">{label}</span>
        <span className="qv">{q.limit == null ? <>{q.used} / <span className="inf">∞</span></> : `${q.used} / ${q.limit}`}</span>
      </div>
      <div className="qtrack"><div className={`qfill ${q.cls}`} style={{ width: q.limit == null ? '12%' : `${q.pct}%` }} /></div>
    </div>
  );

  const stepDone = { bank: banks.length > 0, schema: schemas.length > 0, invite: totalInv > 0 };
  const onboardingActive = !stepDone.invite;

  const schemasForBank = (id) => schemas.filter((s) => s.question_bank_id === id);
  const toggle = (set, setter, id) => { const n = new Set(set); n.has(id) ? n.delete(id) : n.add(id); setter(n); };
  const initials = (company?.name || 'S').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  if (loading) {
    return (
      <>
        <div className="sg-ph"><div><h1>Главная</h1></div></div>
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div></div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="sg-ph"><div><h1>Главная</h1></div></div>
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 flex items-center justify-between gap-4">
          <span>Не удалось загрузить данные</span>
          <button onClick={load} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 flex-shrink-0">Повторить</button>
        </div>
      </>
    );
  }

  return (
    <>
      {/* header */}
      <div className="sg-ph">
        <div>
          <h1>Главная</h1>
          <div className="greet">Обзор по аккаунту {company?.name || ''}</div>
        </div>
        <div className="sg-ph-right">
          <div className="sg-balance-pill"><span className="lbl">Баланс</span> {Math.round(company?.balance ?? 0).toLocaleString('ru-RU')} ₽</div>
          <button className="sg-btn sg-btn-primary sg-btn-sm" onClick={() => navigate('/payment')}>Пополнить</button>
        </div>
      </div>

      {/* quota widget */}
      <div className="sg-quota">
        <QuotaCell label="Базы вопросов" q={qBanks} />
        <QuotaCell label="Тест-схемы" q={qSchemas} />
        <QuotaCell label="Групповые ссылки" q={qGroups} />
        {nearLimit && (
          <div className="sg-quota-upsell">
            <span>Достигнут лимит тарифа — расширьте возможности, чтобы продолжить.</span>
            <button className="sg-btn sg-btn-primary sg-btn-sm" onClick={() => navigate('/payment')}>Сменить тариф</button>
          </div>
        )}
      </div>

      {/* stat cards */}
      <div className="sg-stat-grid">
        <div className="sg-stat"><div className="si"><Ico d={ICONS.bank} /></div><div className="sv">{banks.length}</div><div className="sl">Базы вопросов</div></div>
        <div className="sg-stat"><div className="si"><Ico d={ICONS.schema} /></div><div className="sv">{schemas.length}</div><div className="sl">Тест-схемы</div></div>
        <div className="sg-stat"><div className="si"><Ico d={ICONS.mail} /></div><div className="sv">{totalInv}</div><div className="sl">Приглашений</div></div>
        <div className="sg-stat"><div className="si"><Ico d={ICONS.card} /></div><div className="sv">{Math.round(company?.balance ?? 0).toLocaleString('ru-RU')} ₽</div><div className="sl">Баланс</div></div>
      </div>

      {/* onboarding OR quick actions */}
      {onboardingActive ? (
        <div className="sg-onboard">
          <h3>Запустите первый тест за три шага</h3>
          <div className="osub">Три коротких шага до первого приглашения.</div>
          <div className="sg-steps">
            <div className={`sg-step ${stepDone.bank ? 'done' : ''}`}>
              <span className="sn">{stepDone.bank ? <Check /> : '1'}</span>
              <div><div className="st">Загрузите базу вопросов</div><div className="sd">CSV с вопросами и (при необходимости) изображения</div></div>
            </div>
            <div className={`sg-step ${stepDone.schema ? 'done' : ''}`}>
              <span className="sn">{stepDone.schema ? <Check /> : '2'}</span>
              <div><div className="st">Создайте тест-схему</div><div className="sd">Укажите, сколько вопросов получит кандидат</div></div>
            </div>
            <div className={`sg-step ${stepDone.invite ? 'done' : ''}`}>
              <span className="sn">{stepDone.invite ? <Check /> : '3'}</span>
              <div><div className="st">Отправьте приглашение</div><div className="sd">Персональная или групповая ссылка — каждому уникальный тест</div></div>
            </div>
          </div>
          {!stepDone.bank && <button className="sg-btn sg-btn-primary" onClick={() => navigate('/question-banks')}>Загрузить первую базу вопросов <Ico d={ICONS.arrow} /></button>}
          {stepDone.bank && !stepDone.schema && <button className="sg-btn sg-btn-primary" onClick={() => navigate('/test-schemas')}>Создать тест-схему <Ico d={ICONS.arrow} /></button>}
          {stepDone.schema && !stepDone.invite && <button className="sg-btn sg-btn-primary" onClick={() => navigate('/invitations')}>Создать приглашение <Ico d={ICONS.arrow} /></button>}
        </div>
      ) : (
        <div className="sg-qa-grid">
          <div className="sg-qa q1" onClick={() => navigate('/test-schemas')}><div className="qi"><Ico d={ICONS.plus} /></div><div><div className="qt">Создать тест-схему</div><div className="qd">Соберите тест из ваших баз вопросов</div></div><span className="qarr"><Ico d={ICONS.arrow} /></span></div>
          <div className="sg-qa q2" onClick={() => navigate('/invitations')}><div className="qi"><Ico d={ICONS.invite} /></div><div><div className="qt">Пригласить кандидата</div><div className="qd">Персональная или групповая ссылка</div></div><span className="qarr"><Ico d={ICONS.arrow} /></span></div>
          <div className="sg-qa q3" onClick={() => navigate('/invitations')}><div className="qi"><Ico d={ICONS.chart} /></div><div><div className="qt">Открыть результаты</div><div className="qd">Аналитика и сравнение по группам</div></div><span className="qarr"><Ico d={ICONS.arrow} /></span></div>
        </div>
      )}

      {/* structure tree */}
      {banks.length > 0 && (
        <div className="sg-panel">
          <div className="sg-panel-head"><h3>Обзор структуры</h3></div>
          <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {banks.map((bank) => {
              const bs = schemasForBank(bank.id);
              const open = openBanks.has(bank.id);
              return (
                <div key={bank.id}>
                  <div className="sg-tree-bank" onClick={() => toggle(openBanks, setOpenBanks, bank.id)}>
                    <span className={`sg-tree-chev ${open ? 'open' : ''}`}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d={ICONS.chev} /></svg></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="sg-tree-title">{bank.name}</div>
                      <div className="sg-tree-sub">{bank.questions_count || 0} вопросов{bs.length > 0 ? ` · ${bs.length} схем` : ''}</div>
                    </div>
                  </div>
                  {open && (
                    <div className="sg-tree-children">
                      {bs.length === 0 && <div className="sg-muted">Тест-схем пока нет</div>}
                      {bs.map((schema) => {
                        const sInv = invMap[schema.id] || [];
                        const sOpen = openSchemas.has(schema.id);
                        return (
                          <div key={schema.id}>
                            <div className="sg-tree-schema" onClick={(e) => { e.stopPropagation(); toggle(openSchemas, setOpenSchemas, schema.id); }}>
                              <span className={`sg-tree-chev ${sOpen ? 'open' : ''}`}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d={ICONS.chev} /></svg></span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="sg-tree-title" style={{ fontSize: 13.5 }}>{schema.name}</div>
                                <div className="sg-tree-sub">{schema.num_questions} вопросов{sInv.length > 0 ? ` · ${sInv.length} приглашений` : ''}</div>
                              </div>
                            </div>
                            {sOpen && (
                              <div className="sg-tree-children">
                                {sInv.length === 0 && <div className="sg-muted">Приглашений пока нет</div>}
                                {sInv.map((inv) => {
                                  const st = STATUS[inv.status] || STATUS.pending;
                                  return (
                                    <div key={inv.id} className="sg-tree-inv" onClick={() => navigate(`/test-schemas/${schema.id}/invitations`)}>
                                      <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inv.candidate_title}</div>
                                        <div className="sg-tree-sub">{new Date(inv.created_at).toLocaleDateString('ru-RU')}</div>
                                      </div>
                                      <span className={`sg-badge ${st.cls}`}><span className="bd" />{st.label}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

    </>
  );
}

export default Dashboard;
